import { Modal, Form, Input, Select, InputNumber, Button, Flex } from "antd";
import { useEffect } from "react";
import { S3BackupConfig, CreateS3BackupConfigRequest } from "../../api/types";

type AppOption = { id: string; name: string };

type ConfigFormProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: CreateS3BackupConfigRequest) => Promise<void>;
  loading: boolean;
  initialValues?: S3BackupConfig | null;
  apps: AppOption[];
};

export const ConfigForm = ({
  open,
  onClose,
  onSubmit,
  loading,
  initialValues,
  apps,
}: ConfigFormProps) => {
  const [form] = Form.useForm();
  const isEdit = !!initialValues;

  useEffect(() => {
    if (open) {
      if (initialValues) {
        form.setFieldsValue({
          name: initialValues.name,
          s3Endpoint: initialValues.s3Endpoint,
          s3Bucket: initialValues.s3Bucket,
          s3Region: initialValues.s3Region,
          s3AccessKey: initialValues.s3AccessKey,
          s3SecretKey: "",
          appIds: initialValues.appIds ?? [],
          expirationDays: initialValues.expirationDays,
        });
      } else {
        form.resetFields();
        form.setFieldValue("s3Region", "auto");
        form.setFieldValue("expirationDays", 0);
      }
    }
  }, [open, initialValues]);

  const handleFinish = async (values: any) => {
    const payload: any = {
      name: values.name,
      s3Endpoint: values.s3Endpoint,
      s3Bucket: values.s3Bucket,
      s3Region: values.s3Region || "auto",
      s3AccessKey: values.s3AccessKey,
      appIds: values.appIds ?? [],
      expirationDays: values.expirationDays ?? 0,
    };

    if (!isEdit || values.s3SecretKey) {
      payload.s3SecretKey = values.s3SecretKey;
    }

    await onSubmit(payload as CreateS3BackupConfigRequest);
  };

  return (
    <Modal
      open={open}
      title={isEdit ? "Edit Backup Config" : "Create Backup Config"}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        style={{ marginTop: 16 }}
      >
        <Form.Item
          name="name"
          label="Config Name"
          rules={[{ required: true, message: "Please enter a name" }]}
        >
          <Input placeholder="e.g. Production S3 Backup" />
        </Form.Item>

        <Form.Item
          name="s3Endpoint"
          label="S3 Endpoint"
          rules={[{ required: true, message: "Please enter the S3 endpoint" }]}
        >
          <Input placeholder="https://s3.amazonaws.com" />
        </Form.Item>

        <Form.Item
          name="s3Bucket"
          label="Bucket Name"
          rules={[{ required: true, message: "Please enter the bucket name" }]}
        >
          <Input placeholder="my-log-backups" />
        </Form.Item>

        <Form.Item name="s3Region" label="Region">
          <Input placeholder="auto" />
        </Form.Item>

        <Form.Item
          name="s3AccessKey"
          label="Access Key"
          rules={[{ required: true, message: "Please enter the access key" }]}
        >
          <Input placeholder="Access key ID" />
        </Form.Item>

        <Form.Item
          name="s3SecretKey"
          label={
            isEdit ? "Secret Key (leave blank to keep existing)" : "Secret Key"
          }
          rules={
            isEdit
              ? []
              : [{ required: true, message: "Please enter the secret key" }]
          }
        >
          <Input.Password
            placeholder={isEdit ? "••••••••" : "Secret access key"}
          />
        </Form.Item>

        <Form.Item
          name="appIds"
          label="Applications"
          rules={[
            {
              required: true,
              type: "array",
              min: 1,
              message: "Please select at least one application",
            },
          ]}
        >
          <Select
            mode="multiple"
            placeholder="Select applications"
            optionFilterProp="label"
            options={apps.map((a) => ({ value: a.id, label: a.name }))}
          />
        </Form.Item>

        <Form.Item
          name="expirationDays"
          label="Expiration Days (0 = never expire)"
        >
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>

        <Flex justify="flex-end" gap={8}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {isEdit ? "Save Changes" : "Create Config"}
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};
